import {VERIFICATION_METHODS} from '@chekin/api';
import {useMemo, useState, useEffect} from 'react';
import {useForm, FormProvider} from 'react-hook-form';
import {useDefaultContactData} from 'hooks/iv/useDefaultContactData';
import {ChannelVerification} from './ChannelVerification';
import {
  VerificationStep,
  type ContactsFormTypes as FormTypes,
  type VerificationData,
} from '@chekin/shared/guest-form';

type Props = {
  enableEmail?: boolean;
  enablePhone?: boolean;
  onVerificationComplete?: (data: VerificationData) => void | Promise<void>;
};

const ContactsVerificationComplete = ({
  enableEmail = true,
  enablePhone = true,
  onVerificationComplete,
}: Props) => {
  const {email: defaultEmail, phone: defaultPhone} = useDefaultContactData();

  const [currentStep, setCurrentStep] = useState<VerificationStep>(
    VerificationStep.CONTACTS_INPUT,
  );
  const [channelIndex, setChannelIndex] = useState<number>(0);

  const methods = useForm<FormTypes>({
    mode: 'onChange',
    defaultValues: {
      email: defaultEmail,
      phone: defaultPhone,
    },
  });
  const {setValue, getValues} = methods;

  useEffect(() => {
    if (defaultPhone.number && !getValues('phone')?.number) {
      setValue('phone', defaultPhone);
    }
  }, [defaultPhone, setValue, getValues]);

  const enabledChannels = useMemo(
    () => [
      ...(enableEmail ? ([VERIFICATION_METHODS.email] as const) : []),
      ...(enablePhone ? ([VERIFICATION_METHODS.phone] as const) : []),
    ],
    [enableEmail, enablePhone],
  );

  useEffect(() => {
    if (enabledChannels.length > 0) {
      setChannelIndex(0);
    }
  }, [enabledChannels]);

  const channel = enabledChannels[channelIndex];

  const handleOnVerify = async (data: string, verified: boolean) => {
    const isLastChannel = channelIndex >= enabledChannels.length - 1;
    if (!isLastChannel) {
      setCurrentStep(VerificationStep.CONTACTS_INPUT);
      setChannelIndex(prev => prev + 1);
    }

    await onVerificationComplete?.({type: channel, data, verified});
  };

  const isCodeStep = currentStep === VerificationStep.CODES_VERIFICATION;

  const handleBack = isCodeStep
    ? () => setCurrentStep(VerificationStep.CONTACTS_INPUT)
    : channelIndex > 0
    ? () => setChannelIndex(prev => prev - 1)
    : undefined;

  if (!channel) return null;

  return (
    <FormProvider {...methods}>
      <ChannelVerification
        key={channel}
        channel={channel}
        currentStep={currentStep}
        contactIndex={channelIndex + 1}
        contactCount={enabledChannels.length}
        onGenerate={() => setCurrentStep(VerificationStep.CODES_VERIFICATION)}
        onVerify={handleOnVerify}
        onChangeContacts={() => setCurrentStep(VerificationStep.CONTACTS_INPUT)}
        onBack={handleBack}
      />
    </FormProvider>
  );
};

export {ContactsVerificationComplete};
